import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useResizeDetector } from 'react-resize-detector';
import { ExpandableSearch } from '@rossum/components/expandable-search/ExpandableSearch';
import { IconCustomMenuDown } from '@rossum/ui/icons/tabler';
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { defaultWorkspaceSorting } from '../../../../constants/values';
import { sortingValues } from '../../../../types/user';
import { useWorkspaceSorting } from '../hooks/useWorkspaceSorting';

type Props = {
  searchValue: string;
  onSearchChange: (value: string) => void;
};

const Toolbar = ({ searchValue, onSearchChange }: Props) => {
  const intl = useIntl();
  const { width: toolbarWidth, ref } = useResizeDetector();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { sorting, setSorting } = useWorkspaceSorting();

  return (
    <Stack
      ref={ref}
      direction="row"
      justifyContent="space-between"
      sx={{ py: 1, px: 2, zIndex: 1 }}
      alignItems="center"
    >
      <Stack direction="row" justifyContent="space-between" flex={1}>
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {sortingValues.map(value => (
            <MenuItem
              key={value}
              onClick={() => {
                setSorting(value);
                setAnchorEl(null);
              }}
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: `components.queueWorkspaceControl.sortingSelect.labels.${value}`,
                })}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
        <Button
          color="secondary"
          data-cy="sort-workspace-control"
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ zIndex: 1 }}
          endIcon={
            <SvgIcon fontSize="large">
              <IconCustomMenuDown />
            </SvgIcon>
          }
        >
          {intl.formatMessage({
            id: `components.queueWorkspaceControl.sortingSelect.labels.${
              sorting ?? defaultWorkspaceSorting
            }`,
          })}
        </Button>
        <Stack position="relative">
          <ExpandableSearch
            value={searchValue}
            onChange={onSearchChange}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 9,
              backgroundColor: t => t.palette.background.paper,
            }}
            targetWidth={toolbarWidth}
            placeholder={intl.formatMessage({
              id: 'components.dashboard.queues.search',
            })}
            shouldCollapseOnBlur
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export { Toolbar };
